import { useState, useMemo, Fragment, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faPlus,
  faStore,
  faSpinner,
  faArrowUp,
  faChevronRight,
  faFilter,
  faTimes,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import AddSale from "./shop/AddSale";
import EditSale from "./shop/EditSale";
import SaleDetails from "./shop/SaleDetails";
import Pagination from "../../components/ui/Pagination";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import SearchBar from "../../components/ui/SearchBar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { useAuth, useShops } from "../../hooks";
import { useMyShopsSales } from "../../hooks/useSales";
import { useMyShopsSellers } from "../../hooks/useShopSellers";
import { useMyShopsAvailableStock } from "../../hooks/useStock";
import { toastError } from "../../utils/toast";

const ManageSaleO = () => {
  const { hasPermission } = useAuth();
  const canManageSales = hasPermission(["manage_shop_modules", "manage_sales"]);

  const {
    sales: rawSales,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    shopFilter,
    setShopFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh,
    createSales,
    updateSale,
  } = useMyShopsSales({ per_page: 10 });

  const { shops } = useShops({ per_page: 1000 });
  const { sellers: rawSellers } = useMyShopsSellers({ per_page: 1000 });
  const { stocks: rawAvailableStock } = useMyShopsAvailableStock();

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [expandedProductId, setExpandedProductId] = useState(null);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
    shopFilter: "",
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search to prevent UI reload on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, setSearchTerm, setCurrentPage]);

  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);

  const sales = useMemo(
    () => (Array.isArray(rawSales) ? rawSales : []),
    [rawSales],
  );

  const sellers = useMemo(
    () => (Array.isArray(rawSellers) ? rawSellers : []),
    [rawSellers],
  );

  const availableSaleStocks = useMemo(
    () => (Array.isArray(rawAvailableStock) ? rawAvailableStock : []),
    [rawAvailableStock],
  );

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0 TSH";
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const salesWithDeltas = useMemo(() => {
    const rows = Array.isArray(sales) ? sales.slice() : [];
    if (rows.length === 0) return [];

    const byStock = new Map();
    rows.forEach((sale) => {
      const key = sale.stockId;
      if (!byStock.has(key)) byStock.set(key, []);
      byStock.get(key).push(sale);
    });

    const withDeltas = [];

    byStock.forEach((stockRows) => {
      const chronological = stockRows.slice().sort((a, b) => {
        const at = new Date(a.saleDate || a.created_at || 0).getTime();
        const bt = new Date(b.saleDate || b.created_at || 0).getTime();
        return at - bt;
      });

      const totalItems = Number(chronological[0]?.totalItemsInStock ?? 0);
      let previousRemaining = null;

      chronological.forEach((sale) => {
        const remaining = Number(sale.no_of_unsold ?? 0);

        const perSaleSold =
          previousRemaining === null
            ? Math.max(0, totalItems - remaining)
            : Math.max(0, previousRemaining - remaining);

        previousRemaining = remaining;

        const pricePerItem = Number(sale.actualSellingPrice ?? 0);
        const costPerItem = Number(sale.costPerItem ?? 0);

        const perSaleRevenue = perSaleSold * pricePerItem;
        const perSaleProfit = perSaleSold * (pricePerItem - costPerItem);

        withDeltas.push({
          ...sale,
          perSaleSold,
          perSaleRevenue,
          perSaleProfit,
        });
      });
    });

    return withDeltas;
  }, [sales]);

  const productGroups = useMemo(() => {
    const map = new Map();

    salesWithDeltas.forEach((sale) => {
      const productId = sale.productId || sale.productName;
      if (!map.has(productId)) {
        map.set(productId, {
          productId,
          productName: sale.productName || "Unknown product",
          productCategory: sale.productCategory || null,
          productType: sale.productType || null,
          sales: [],
          stockMap: new Map(),
          shops: new Set(),
          sellers: new Set(),
        });
      }
      const group = map.get(productId);
      group.sales.push(sale);
      if (sale.shopName) group.shops.add(sale.shopName);
      if (sale.sellerName) group.sellers.add(sale.sellerName);

      const stockId = sale.stockId || `stock-${group.stockMap.size + 1}`;
      if (!group.stockMap.has(stockId)) {
        group.stockMap.set(stockId, {
          stockId,
          shopName: sale.shopName || "-",
          productName: sale.productName || null,
          productCategory: sale.productCategory || null,
          productType: sale.productType || null,
          packageType: sale.packageType || null,
          sales: [],
        });
      }
      group.stockMap.get(stockId).sales.push(sale);
    });

    return Array.from(map.values()).map((group) => {
      let totalItems = 0;
      let itemsSold = 0;
      let remaining = 0;
      let revenue = 0;
      let profit = 0;

      group.sales.forEach((s) => {
        totalItems += Number(s.totalItemsInStock ?? 0);
        itemsSold += Number(s.perSaleSold ?? 0);
        remaining += Number(s.no_of_unsold ?? 0);
        revenue += Number(s.perSaleRevenue ?? 0);
        profit += Number(s.perSaleProfit ?? 0);
      });

      return {
        ...group,
        salesCount: group.sales.length,
        shopCount: group.shops.size,
        sellerCount: group.sellers.size,
        totalItems,
        itemsSold,
        remaining,
        revenue,
        profit,
      };
    });
  }, [salesWithDeltas]);

  const buildStockRows = (group) => {
    if (!group?.stockMap) return [];
    const rows = [];
    let idx = 1;

    group.stockMap.forEach((stock) => {
      let itemsSold = 0;
      let totalItemsInStock = 0;
      let revenue = 0;

      stock.sales.forEach((s) => {
        itemsSold += Number(s.perSaleSold ?? 0);
        totalItemsInStock = Math.max(
          totalItemsInStock,
          Number(s.totalItemsInStock ?? 0),
        );
        revenue += Number(s.perSaleRevenue ?? 0);
      });

      rows.push({
        stockNo: idx++,
        stockId: stock.stockId,
        shopName: stock.shopName,
        productName: stock.productName,
        productCategory: stock.productCategory,
        productType: stock.productType,
        packageType: stock.packageType,
        salesCount: stock.sales.length,
        totalItemsInStock,
        itemsSold,
        revenue,
        sales: stock.sales,
      });
    });

    return rows;
  };

  const summaryStats = useMemo(() => {
    let totalRevenue = 0;
    let totalProfit = 0;

    salesWithDeltas.forEach((sale) => {
      totalRevenue += Number(sale.perSaleRevenue || 0);
      totalProfit += Number(sale.perSaleProfit || 0);
    });

    return {
      total: pagination?.total ?? salesWithDeltas.length,
      totalRevenue,
      totalProfit,
    };
  }, [salesWithDeltas, pagination]);

  const handleAddSale = async (saleItems) => {
    const payloads = saleItems.map((item) => ({
      sellerId: item.sellerId,
      stockId: item.stockId,
      selling_price: item.sellingPrice,
      no_of_unsold: item.noOfUnSold,
      status: item.status?.toLowerCase() || "active",
    }));

    try {
      await createSales(payloads);
      setIsAddModalOpen(false);
    } catch (err) {
      toastError(
        err?.response?.data?.message || err?.message || "Failed to record sale",
      );
    }
  };

  const handleUpdateSale = async (saleData) => {
    try {
      const id = saleData.salesId || saleData.id;
      await updateSale(id, saleData);
      setIsEditModalOpen(false);
      setSelectedSale(null);
      setIsViewModalOpen(false);
    } catch (err) {
      toastError(
        err?.response?.data?.message || err?.message || "Failed to update sale",
      );
    }
  };

  const handleEditSaleFromDetails = (saleRow) => {
    if (!canManageSales) return;
    setSelectedSale(saleRow);
    setIsEditModalOpen(true);
  };

  const toggleExpanded = (productId) => {
    setExpandedProductId((prev) => (prev === productId ? null : productId));
  };

  const openStockSalesInDetails = (group, stockRow) => {
    const allSales = Array.isArray(stockRow.sales) ? stockRow.sales : [];
    if (allSales.length === 0) return;

    const newest = allSales.slice().sort((a, b) => {
      const at = new Date(a.saleDate || a.created_at || 0).getTime();
      const bt = new Date(b.saleDate || b.created_at || 0).getTime();
      return bt - at;
    })[0];

    const seed = {
      ...newest,
      productName: group.productName || stockRow.productName,
      productCategory: group.productCategory || stockRow.productCategory,
      productType: group.productType || stockRow.productType,
      packageType: stockRow.packageType || newest.packageType,
      shopName: stockRow.shopName || newest.shopName,
      stockId: stockRow.stockId,
      sellers: [],
    };

    setSelectedSale(seed);
    setIsViewModalOpen(true);
  };

  // --- Advanced Filter Handlers ---
  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
      shopFilter: shopFilter || "",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setShopFilter(
      tempFilters.shopFilter === "all" ? "" : tempFilters.shopFilter,
    );
    setIsFilterApplied(
      !!(
        tempFilters.startDate ||
        tempFilters.endDate ||
        tempFilters.shopFilter !== "all"
      ),
    );
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
      shopFilter: "",
    });
    setStartDate("");
    setEndDate("");
    setShopFilter("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  if (loading && sales.length === 0) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading sales...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center">
        <p className="text-red-600 text-sm">
          Failed to load sales. Please try again.
        </p>
        <Button
          onClick={() => refresh()}
          className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  const totalItems = pagination?.total ?? sales.length;
  const totalPages = pagination?.last_page ?? 1;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage sales
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Track and manage all sales transactions
              </p>
            </div>
          </div>
          {canManageSales && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add sale
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="text-blue-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Sales</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.total || 0}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Revenue</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {formatCurrency(summaryStats.totalRevenue || 0)}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center col-span-2 sm:col-span-1">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-orange-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Profit</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600">
            {formatCurrency(summaryStats.totalProfit || 0)}
          </p>
        </Card>
      </div>

      {/* Main Toolbar: Search Left, Advanced Filter Right */}
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          placeholder="Search by product, category, type, seller, or shop..."
          className="flex-1"
        />
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <div className="hidden md:block">
        <TableWrapper
          title="sales"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  #
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Product
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                  Sellers
                </th>
                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                  Sold
                </th>
                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                  Sales
                </th>
                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Revenue
                </th>
                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Profit
                </th>
                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {productGroups.length > 0 ? (
                productGroups.map((group, i) => {
                  const globalIndex = i + 1;
                  const isExpanded = expandedProductId === group.productId;
                  const stockRows = isExpanded ? buildStockRows(group) : [];

                  return (
                    <Fragment key={group.productId}>
                      <tr
                        onClick={() => toggleExpanded(group.productId)}
                        className={`group cursor-pointer transition-colors ${
                          isExpanded ? "bg-blue-50/70" : "hover:bg-blue-50/60"
                        }`}
                        title="Click to view stocks that produced these sales"
                      >
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-600 font-medium">
                          {globalIndex}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-800">
                          <div>
                            <p className="font-medium">{group.productName}</p>
                            <p className="text-sm text-orange-600 font-medium">
                              {group.productCategory || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {group.shopCount}{" "}
                              {group.shopCount === 1 ? "shop" : "shops"} ·{" "}
                              {group.salesCount}{" "}
                              {group.salesCount === 1 ? "sale" : "sales"}
                            </p>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-600 hidden lg:table-cell">
                          {group.sellerCount}{" "}
                          {group.sellerCount === 1 ? "seller" : "sellers"}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs font-semibold text-gray-900 hidden md:table-cell">
                          {group.itemsSold}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs font-semibold text-gray-900 hidden md:table-cell">
                          {group.salesCount}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm font-semibold text-green-600 hidden sm:table-cell">
                          {formatCurrency(group.revenue)}
                        </td>
                        <td
                          className={`py-2 sm:py-3 px-2 sm:px-4 text-right text-sm font-semibold hidden sm:table-cell ${
                            group.profit > 0
                              ? "text-green-600"
                              : group.profit < 0
                                ? "text-red-600"
                                : "text-gray-600"
                          }`}
                        >
                          {group.profit >= 0
                            ? formatCurrency(group.profit)
                            : `(${formatCurrency(Math.abs(group.profit))})`}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className={`h-3.5 w-3.5 transition-all ${
                              isExpanded
                                ? "text-blue-700 rotate-90"
                                : "text-gray-600 group-hover:text-blue-700 group-hover:translate-x-0.5"
                            }`}
                          />
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={8}
                            className="bg-blue-50/40 px-3 sm:px-6 py-3 border-t border-b border-blue-100"
                          >
                            <div className="rounded-lg border border-blue-100 bg-white overflow-hidden shadow-sm">
                              <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-white border-b border-blue-100 flex items-center justify-between">
                                <p className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                                  <FontAwesomeIcon
                                    icon={faStore}
                                    className="h-3 w-3 text-blue-600"
                                  />
                                  Stocks that produced these sales
                                </p>
                                <span className="text-sm text-gray-600">
                                  {stockRows.length}{" "}
                                  {stockRows.length === 1 ? "stock" : "stocks"}
                                </span>
                              </div>

                              <div className="divide-y divide-gray-50">
                                {stockRows.map((row) => (
                                  <button
                                    key={row.stockId}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openStockSalesInDetails(group, row);
                                    }}
                                    className="w-full text-left px-3 sm:px-4 py-2.5 hover:bg-blue-50/60 transition-colors group/shop"
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-900 text-white text-sm font-semibold">
                                            Stock #{row.stockNo}
                                          </span>
                                          <FontAwesomeIcon
                                            icon={faStore}
                                            className="h-3 w-3 text-blue-600"
                                          />
                                          {row.shopName}
                                        </p>
                                        <p className="text-sm text-gray-600 mt-0.5">
                                          {row.salesCount}{" "}
                                          {row.salesCount === 1
                                            ? "sale"
                                            : "sales"}{" "}
                                          · {row.totalItemsInStock} Total items
                                          · Sold{" "}
                                          <span className="text-green-600 font-semibold">
                                            {row.itemsSold} items
                                          </span>{" "}
                                          ·{" "}
                                          <span className="text-green-600 font-semibold">
                                            {formatCurrency(row.revenue)}
                                          </span>
                                        </p>
                                      </div>
                                      <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className="h-3 w-3 text-gray-600 group-hover/shop:text-blue-700 group-hover/shop:translate-x-0.5 transition-all"
                                      />
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-6 sm:py-8">
                    <EmptyState
                      title="No sales found"
                      message={
                        searchTerm || shopFilter || isFilterApplied
                          ? "Try adjusting your search or filters"
                          : canManageSales
                            ? "Add your first sale to get started"
                            : "No sales yet"
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrapper>
      </div>

      <div className="md:hidden">
        {productGroups.length > 0 ? (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 px-1">
              <span>
                Showing {productGroups.length}{" "}
                {productGroups.length === 1 ? "product" : "products"}
              </span>
              <span>
                Page {currentPage} of {totalPages || 1}
              </span>
            </div>

            <div className="space-y-3">
              {productGroups.map((group, i) => {
                const isExpanded = expandedProductId === group.productId;
                const stockRows = isExpanded ? buildStockRows(group) : [];

                return (
                  <div
                    key={group.productId}
                    className="bg-white border-2 border-blue-900 rounded-md shadow-sm overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(group.productId)}
                      className="w-full text-left p-3 active:bg-blue-50/60 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-white font-medium bg-blue-900 px-2 rounded-full">
                              {i + 1}
                            </span>
                            <h4 className="text-sm font-semibold text-gray-900 truncate">
                              {group.productName}
                            </h4>
                          </div>

                          <p className="text-xs text-orange-600 font-medium mb-1">
                            {group.productCategory || "N/A"}
                          </p>

                          <p className="text-xs text-gray-600">
                            {group.shopCount}{" "}
                            {group.shopCount === 1 ? "shop" : "shops"} ·{" "}
                            {group.salesCount}{" "}
                            {group.salesCount === 1 ? "sale" : "sales"} ·{" "}
                            {group.sellerCount}{" "}
                            {group.sellerCount === 1 ? "seller" : "sellers"}
                          </p>

                          <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-gray-600">Sold:</span>
                              <span className="font-semibold text-gray-900">
                                {group.itemsSold}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-gray-600">Revenue:</span>
                              <span className="font-semibold text-green-600">
                                {formatCurrency(group.revenue)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-gray-600">Profit:</span>
                              <span
                                className={`font-semibold ${
                                  group.profit > 0
                                    ? "text-green-600"
                                    : group.profit < 0
                                      ? "text-red-600"
                                      : "text-gray-600"
                                }`}
                              >
                                {group.profit >= 0
                                  ? formatCurrency(group.profit)
                                  : `(${formatCurrency(Math.abs(group.profit))})`}
                              </span>
                            </div>
                          </div>
                        </div>

                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className={`h-3.5 w-3.5 mt-2 shrink-0 transition-all ${
                            isExpanded
                              ? "text-blue-700 rotate-90"
                              : "text-gray-600"
                          }`}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-blue-100 bg-blue-50/40 p-2">
                        <div className="rounded-lg border border-blue-100 bg-white overflow-hidden">
                          <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-white border-b border-blue-100 flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-700">
                              Stocks that produced these sales
                            </p>
                            <span className="text-xs text-gray-600">
                              {stockRows.length}
                            </span>
                          </div>
                          <div className="divide-y divide-gray-50">
                            {stockRows.map((row) => (
                              <button
                                key={row.stockId}
                                type="button"
                                onClick={() =>
                                  openStockSalesInDetails(group, row)
                                }
                                className="w-full text-left px-3 py-2.5 active:bg-blue-50 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-900 text-white text-sm font-semibold">
                                        Stock #{row.stockNo}
                                      </span>
                                      <span className="text-xs font-medium text-gray-900 truncate">
                                        {row.shopName}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {row.salesCount}{" "}
                                      {row.salesCount === 1 ? "sale" : "sales"}{" "}
                                      · {row.totalItemsInStock} items · Sold{" "}
                                      <span className="text-green-600 font-semibold">
                                        {row.itemsSold}
                                      </span>{" "}
                                      ·{" "}
                                      <span className="text-green-600 font-semibold">
                                        {formatCurrency(row.revenue)}
                                      </span>
                                    </p>
                                  </div>
                                  <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className="h-3 w-3 text-gray-600 mt-1.5 shrink-0"
                                  />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <EmptyState
              title="No sales found"
              message={
                searchTerm || shopFilter || isFilterApplied
                  ? "Try adjusting your search or filters"
                  : canManageSales
                    ? "Add your first sale to get started"
                    : "No sales yet"
              }
            />
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canManageSales && isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-start justify-center p-4">
          <div className="w-full max-w-6xl mt-8 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <AddSale
              onAdd={handleAddSale}
              onClose={() => setIsAddModalOpen(false)}
              sellers={sellers}
              stockData={availableSaleStocks}
            />
          </div>
        </div>
      )}

      {isViewModalOpen && selectedSale && (
        <SaleDetails
          sale={selectedSale}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedSale(null);
          }}
          onEdit={(sale) => {
            if (!canManageSales) return;
            setIsViewModalOpen(false);
            setSelectedSale(sale);
            setIsEditModalOpen(true);
          }}
          onEditSale={handleEditSaleFromDetails}
          canManageSales={canManageSales}
        />
      )}

      {canManageSales && isEditModalOpen && selectedSale && (
        <EditSale
          sale={selectedSale}
          onUpdate={handleUpdateSale}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedSale(null);
          }}
          sellers={sellers}
          stockData={availableSaleStocks}
        />
      )}

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Sales
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faStore}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Shop
                </label>
                <Select
                  value={tempFilters.shopFilter || "all"}
                  onValueChange={(val) =>
                    setTempFilters({ ...tempFilters, shopFilter: val })
                  }
                >
                  <SelectTrigger className="w-full h-10 border border-gray-300 rounded-lg bg-gray-50/50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <SelectValue placeholder="All Shops" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Shops</SelectItem>
                    {safeShops.map((shop) => (
                      <SelectItem key={shop.shopId} value={shop.shopId}>
                        {shop.shop_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSaleO;
